import React, { useState, useEffect, useRef } from 'react';
import { LANDING_STRINGS, LandingLanguage } from '../constants/landingTranslations';
import { ChevronDown, Sparkles, Brain, Target, TrendingUp, MessageCircle, Zap, Globe, ArrowRight, Shield, Check, X as XIcon, Plus, Minus, Lightbulb, Search, Calendar, Heart, Users, Tag, Home } from 'lucide-react';
import heroImage from '@assets/generated_images/triumphant_person_on_mountain_peak.png';
import EgovexLogo from '../components/EgovexLogo';
import { trackPageView } from '../src/lib/analytics';
import { AI_PERSONALITIES } from '../shared/aiPersonalities';
import type { Language } from '../types';
import GoalProgressRing from '../components/GoalProgressRing';
import WeeklyRhythmChart from '../components/WeeklyRhythmChart';

interface LandingViewProps {
  onLogin: () => void;
  onRegister?: () => void;
  onShowPrivacy?: () => void;
  onShowTerms?: () => void;
  lang?: LandingLanguage;
  onLangChange?: (lang: LandingLanguage) => void;
}

const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const LandingView: React.FC<LandingViewProps> = ({
  onLogin,
  onRegister,
  onShowPrivacy,
  onShowTerms,
  lang: externalLang,
  onLangChange
}) => {
  const [internalLang, setInternalLang] = useState<LandingLanguage>('en');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [pricingToggle, setPricingToggle] = useState<'monthly' | 'annual'>('annual');

  // Touch swipe state for hero carousel
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  useEffect(() => {
    trackPageView('/', 'Landing', false);
  }, []);

  const lang = externalLang || internalLang;
  const setLang = (newLang: LandingLanguage) => {
    localStorage.setItem('egovex_preferred_language', newLang);
    if (onLangChange) {
      onLangChange(newLang);
    } else {
      setInternalLang(newLang);
    }
  };

  // Get translations - with fallback for languages without full translations
  const t = LANDING_STRINGS[lang] || LANDING_STRINGS.en;
  const tHeroRot = t.heroRotating || LANDING_STRINGS.en.heroRotating;
  const tSocial = t.socialProof || LANDING_STRINGS.en.socialProof;
  const tPricing = t.pricing || LANDING_STRINGS.en.pricing;
  const tTrust = t.trust || LANDING_STRINGS.en.trust;
  const tFaq = t.faq || LANDING_STRINGS.en.faq;
  const tPersonalities = t.personalities || LANDING_STRINGS.en.personalities;
  const tLabels = t.showcaseLabels || LANDING_STRINGS.en.showcaseLabels;
  const tPrivacy = t.privacy || LANDING_STRINGS.en.privacy;

  // Rotating headlines effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeadline((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Touch swipe handlers for hero carousel
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const swipeDistance = touchStartX - touchEndX;
    const minSwipeDistance = 50; // Minimum distance to trigger swipe

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left - go to next headline
        setCurrentHeadline((prev) => (prev + 1) % 4);
      } else {
        // Swiped right - go to previous headline
        setCurrentHeadline((prev) => (prev - 1 + 4) % 4);
      }
    }

    // Reset touch positions
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const languageLabels: Record<LandingLanguage, string> = {
    en: 'English',
    it: 'Italiano',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français'
  };

  const headlines = [
    tHeroRot.headline1,
    tHeroRot.headline2,
    tHeroRot.headline3,
    tHeroRot.headline4
  ];

  const socialProofSection = useIntersectionObserver();
  const personalitiesSection = useIntersectionObserver();
  const pricingSection = useIntersectionObserver();
  const trustSection = useIntersectionObserver();
  const faqSection = useIntersectionObserver();

  return (
    <div className="min-h-screen bg-white text-[#1D1D1F] overflow-x-hidden font-sans">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-slide-up { animation: slide-up 0.8s ease-out forwards; }
        .animate-fade-in { animation: fade-in 0.6s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.6s ease-out forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
        @media (prefers-reduced-motion: reduce) {
          .animate-slide-up, .animate-fade-in, .animate-scale-in, .animate-float, .animate-gradient {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-black/[0.04]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <EgovexLogo size={32} className="flex-shrink-0" />
              <span className="font-black text-lg sm:text-xl tracking-tighter truncate">Egovex</span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4">
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-2 sm:py-2.5 text-sm text-black/50 hover:text-black transition-colors font-medium"
                >
                  <Globe className="w-4 h-4 flex-shrink-0" />
                  <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                </button>
                {showLangMenu && (
                  <div className="absolute right-0 top-full mt-1 glass rounded-2xl shadow-xl border border-black/[0.04] py-2 min-w-[140px]">
                    {Object.entries(languageLabels).map(([code, label]) => (
                      <button
                        key={code}
                        onClick={() => { setLang(code as LandingLanguage); setShowLangMenu(false); }}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-black/5 transition-colors font-medium ${lang === code ? 'text-indigo-600' : 'text-black/70'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Login button - hidden on very small screens */}
              <button
                onClick={onLogin}
                className="hidden sm:block text-sm sm:text-base font-bold text-black/60 hover:text-black transition-colors px-3 sm:px-4 py-2.5 whitespace-nowrap"
              >
                {t.nav.login}
              </button>

              {/* CTA button - responsive layout */}
              <button
                onClick={onRegister || onLogin}
                className="flex items-center gap-1 sm:gap-1.5 md:gap-2 text-xs sm:text-sm md:text-base font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full hover:opacity-90 transition-all active:scale-95 shadow-lg shadow-indigo-200"
              >
                <span className="whitespace-nowrap">{t.nav.getStarted}</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Rotating Headlines */}
      <section className="relative min-h-screen flex flex-col pt-16">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-white">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-indigo-200 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="relative flex-1 flex flex-col justify-center pb-16 sm:pb-24 pt-12 lg:pt-16 lg:pb-32">
          <div className="max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-indigo-600 text-[11px] font-black uppercase tracking-[0.15em] mb-8 border border-indigo-100">
              <Sparkles className="w-4 h-4" />
              {t.hero.tagline}
            </div>

            {/* Rotating Headlines - Swipeable */}
            <div
              className="mb-6 lg:mb-10 h-48 sm:h-40 lg:h-52 flex items-center justify-center cursor-grab active:cursor-grabbing touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="animate-fade-in">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-black tracking-tighter leading-[1.1] mb-4 lg:mb-6 text-[#1D1D1F]">
                  {headlines[currentHeadline].title}
                </h1>
                <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-black/60 leading-relaxed max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto font-medium">
                  {headlines[currentHeadline].subtitle}
                </p>
              </div>
            </div>

            {/* Headline Progress Dots */}
            <div className="flex items-center justify-center gap-2 mb-10 lg:mb-14">
              {[0, 1, 2, 3].map((i) => (
                <button
                  key={i}
                  onClick={() => setCurrentHeadline(i)}
                  className={`h-2 lg:h-2.5 rounded-full transition-all ${
                    i === currentHeadline ? 'w-8 lg:w-10 bg-indigo-600' : 'w-2 lg:w-2.5 bg-black/20'
                  }`}
                />
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center mb-8 lg:mb-12">
              <button
                onClick={onRegister || onLogin}
                className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 lg:px-14 py-5 lg:py-6 rounded-2xl lg:rounded-3xl text-lg lg:text-xl font-black tracking-tight hover:opacity-90 transition-all active:scale-95 shadow-2xl shadow-indigo-200 flex items-center justify-center gap-3"
              >
                <Sparkles className="w-5 h-5 lg:w-6 lg:h-6" />
                {t.hero.cta}
                <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <p className="text-black/50 text-sm font-medium flex items-center justify-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              {tHeroRot.trustBadge}
            </p>

            {/* Trust Pills */}
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6 mt-8 lg:mt-12">
              {[
                { icon: Brain, label: tHeroRot.trustPills.aiPowered },
                { icon: Shield, label: tHeroRot.trustPills.private },
                { icon: Globe, label: tHeroRot.trustPills.languages },
                { icon: TrendingUp, label: tHeroRot.trustPills.patterns }
              ].map((pill, idx) => (
                <div key={idx} className="flex items-center gap-2 lg:gap-3 px-4 lg:px-6 py-2 lg:py-3 bg-white/60 backdrop-blur-sm rounded-full border border-black/5 text-sm lg:text-base font-bold text-[#1D1D1F]">
                  <pill.icon className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-600" />
                  {pill.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section
        ref={socialProofSection.ref}
        className={`py-16 bg-[#FAFAFA] border-y border-black/[0.04] ${socialProofSection.isVisible ? 'animate-fade-in' : 'opacity-0'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-lg font-bold text-black/60 mb-8">{tSocial.trusted}</p>
          <div className="grid grid-cols-3 gap-8">
            <div>
              <p className="text-4xl font-black text-indigo-600 mb-2">2,800+</p>
              <p className="text-sm font-medium text-black/50">{tSocial.users}</p>
            </div>
            <div>
              <p className="text-4xl font-black text-purple-600 mb-2">45+</p>
              <p className="text-sm font-medium text-black/50">{tSocial.countries}</p>
            </div>
            <div>
              <p className="text-4xl font-black text-emerald-600 mb-2">50K+</p>
              <p className="text-sm font-medium text-black/50">{tSocial.moments}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase - See it in Action */}
      <section className="py-24 sm:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-20">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-indigo-600 mb-4 block">
              {tLabels.seeInAction}
            </span>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4 text-[#1D1D1F]">
              {t.features.title}
            </h2>
            <p className="text-lg text-black/60 max-w-2xl mx-auto font-medium">
              {t.features.subtitle}
            </p>
          </div>

          {/* Feature Showcases */}
          <div className="space-y-24">
            {/* ========================================
                PHASE 1: SELF-KNOWLEDGE (Know Thyself)
                ======================================== */}

            {/* 1. Mirror/Synthesis Showcase - MOST IMPACTFUL */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Mirror UI Mockup */}
              <div className="order-2 lg:order-1">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-purple-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4 flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-600" />
                      <span className="text-xs font-black uppercase tracking-wider text-purple-600">
                        {tLabels.psychologicalProfile}
                      </span>
                    </div>
                    <p className="text-sm text-[#1D1D1F] leading-relaxed mb-4">
                      {t.showcaseMirror.exampleText}
                    </p>
                    <div className="flex gap-2 mb-4">
                      <span className="text-xs font-bold text-purple-700 bg-purple-100 px-3 py-1.5 rounded-full">
                        {t.showcaseMirror.tag1}
                      </span>
                      <span className="text-xs font-bold text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-full">
                        {t.showcaseMirror.tag2}
                      </span>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm font-medium text-[#1D1D1F] leading-relaxed">
                          {t.showcaseMirror.exampleQuestion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Description */}
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-purple-600">
                    {t.showcaseMirror.label}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {t.showcaseMirror.title}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {t.showcaseMirror.desc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseMirror.bullet1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseMirror.bullet2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseMirror.bullet3}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Values Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Description */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-rose-600">
                    {t.showcaseValues.label}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {t.showcaseValues.title}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {t.showcaseValues.desc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseValues.bullet1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseValues.bullet2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseValues.bullet3}</span>
                  </div>
                </div>
              </div>
              {/* Values UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-rose-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Heart className="w-5 h-5 text-rose-600" />
                        <span className="text-xs font-black uppercase tracking-wider text-rose-600">
                          {tLabels.yourValues}
                        </span>
                      </div>
                      <button className="text-xs font-bold text-rose-600 flex items-center gap-1">
                        <Plus className="w-4 h-4" />
                        {tLabels.addButton}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {[
                        t.showcaseValues.exampleValue1,
                        t.showcaseValues.exampleValue2,
                        t.showcaseValues.exampleValue3,
                        t.showcaseValues.exampleValue4,
                        t.showcaseValues.exampleValue5,
                        t.showcaseValues.exampleValue6
                      ].map((value, idx) => (
                        <div key={idx} className="group flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-full">
                          <Tag className="w-3 h-3 text-rose-600" />
                          <span className="text-sm font-medium text-[#1D1D1F]">{value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-3 border border-rose-100">
                      <p className="text-xs text-black/60 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-rose-600" />
                        {tLabels.aiSuggestValues}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. People Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* People UI Mockup */}
              <div className="order-2 lg:order-1">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-cyan-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-cyan-600" />
                      <span className="text-xs font-black uppercase tracking-wider text-cyan-600">
                        {tLabels.importantPeople}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {/* Person 1: Sarah */}
                      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                            S
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-black text-[#1D1D1F]">Sarah</p>
                              <span className="text-xs text-cyan-700 bg-cyan-100 px-2 py-1 rounded-full font-bold">
                                142 {t.showcasePeople.mentioned}
                              </span>
                            </div>
                            <p className="text-xs text-black/60 mb-2">{t.showcasePeople.relationship1}</p>
                            <div className="flex gap-1.5">
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full text-black/50">
                                {tLabels.family}
                              </span>
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full text-black/50">
                                {tLabels.love}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Person 2: Marco */}
                      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                            M
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-black text-[#1D1D1F]">Marco</p>
                              <span className="text-xs text-cyan-700 bg-cyan-100 px-2 py-1 rounded-full font-bold">
                                78 {t.showcasePeople.mentioned}
                              </span>
                            </div>
                            <p className="text-xs text-black/60 mb-2">{t.showcasePeople.relationship2}</p>
                            <div className="flex gap-1.5">
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full text-black/50">
                                {tLabels.work}
                              </span>
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full text-black/50">
                                {tLabels.growth}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Description */}
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-cyan-600">
                    {t.showcasePeople.label}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {t.showcasePeople.title}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {t.showcasePeople.desc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcasePeople.bullet1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcasePeople.bullet2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcasePeople.bullet3}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                PHASE 3: GROWTH & ACTION (Make Better Choices)
                Note: Goals showcase here, ideally after Pattern Detection + Life Intelligence
                ======================================== */}

            {/* 4. Goals Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Description */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-emerald-600">
                    {t.showcaseGoals.label}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {t.showcaseGoals.title}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {t.showcaseGoals.desc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseGoals.bullet1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseGoals.bullet2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{t.showcaseGoals.bullet3}</span>
                  </div>
                </div>
              </div>
              {/* Goals UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-emerald-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-emerald-600" />
                        <span className="text-xs font-black uppercase tracking-wider text-emerald-600">
                          {tLabels.yourGoals}
                        </span>
                      </div>
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                        4 {tLabels.active}
                      </span>
                    </div>
                    <div className="space-y-2.5">
                      {[
                        { goal: t.showcaseGoals.exampleGoal1, progress: 65 },
                        { goal: t.showcaseGoals.exampleGoal2, progress: 40 },
                        { goal: t.showcaseGoals.exampleGoal3, progress: 80 },
                        { goal: t.showcaseGoals.exampleGoal4, progress: 55 }
                      ].map((item, idx) => (
                        <div key={idx} className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 border border-emerald-100">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="w-5 h-5 rounded-full border-2 border-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                              {item.progress >= 75 && <Check className="w-3 h-3 text-emerald-600" />}
                            </div>
                            <p className="text-sm font-medium text-[#1D1D1F] leading-snug flex-1">
                              {item.goal}
                            </p>
                          </div>
                          <div className="ml-7">
                            <div className="w-full h-1.5 bg-white rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <p className="text-xs text-emerald-700 mt-1 font-bold">{item.progress}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                PHASE 2: PATTERNS & INSIGHTS (See the Bigger Picture)
                ======================================== */}

            {/* 5. Pattern Detection Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-pink-600">{tLabels.patternDetection}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">{t.features.awareness.title}</h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">{t.features.awareness.desc}</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.aiFindsPatterns}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.seeConnectionsMoments}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.getPersonalizedSuggestions}</span>
                  </div>
                </div>
              </div>
              {/* Pattern UI Mockup */}
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-pink-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-pink-600" />
                        <span className="text-sm font-black uppercase tracking-wide text-gray-500">{tLabels.patternsIdentified}</span>
                      </div>
                      <span className="text-xs font-bold text-pink-600 bg-pink-50 px-2 py-1 rounded-full">9</span>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-black text-[#1D1D1F]">{tLabels.joyInConnection}</span>
                          <span className="text-xs font-bold text-pink-600 bg-white px-2 py-1 rounded-full">90%</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-pink-700 font-bold px-2 py-0.5 bg-pink-100 rounded-full">{tLabels.emotional}</span>
                          <span className="text-xs text-black/50">• 12x {tLabels.observed}</span>
                        </div>
                        <p className="text-xs text-black/60 leading-relaxed">
                          {tLabels.patternJoyDescription}
                        </p>
                        <div className="mt-3 flex items-start gap-2 bg-yellow-50 rounded-lg p-2">
                          <Sparkles className="w-3 h-3 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-yellow-800 font-medium italic leading-relaxed">
                            {tLabels.patternJoySuggestion}
                          </p>
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-black text-[#1D1D1F]">{tLabels.workStress}</span>
                          <span className="text-xs font-bold text-blue-600 bg-white px-2 py-1 rounded-full">85%</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-blue-700 font-bold px-2 py-0.5 bg-blue-100 rounded-full">{tLabels.situational}</span>
                          <span className="text-xs text-black/50">• 8x {tLabels.observed}</span>
                        </div>
                        <p className="text-xs text-black/60">
                          {tLabels.patternWorkDescription}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 text-center text-xs text-black/40 font-medium">
                      {tLabels.swipeForMore}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 6. Decision Intelligence Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Decision UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-indigo-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-black text-[#1D1D1F]">Path</span>
                        <span className="text-xs text-black/40 font-medium uppercase tracking-wide">{tLabels.resolveDoubtsWithData}</span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-sm font-bold text-[#1D1D1F] mb-2">{tLabels.compareOptions}</p>
                        <p className="text-xs text-black/60">
                          {tLabels.decisionOptionDescription}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{tLabels.decisionAnalysis}</span>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Target className="w-3.5 h-3.5 text-indigo-600" />
                          </div>
                          <span className="text-lg font-black text-indigo-600">85%</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                          <p className="text-xs font-black text-emerald-700 mb-2 uppercase tracking-wide">{tLabels.pros}</p>
                          <div className="space-y-1">
                            <p className="text-xs text-emerald-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Allineato con desiderio sicurezza finanziaria' : 'Aligned with financial security desire'}</span>
                            </p>
                            <p className="text-xs text-emerald-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Permette mobilità e libertà' : 'Allows mobility and freedom'}</span>
                            </p>
                            <p className="text-xs text-emerald-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Alto potenziale guadagno' : 'High earning potential'}</span>
                            </p>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <p className="text-xs font-black text-red-700 mb-2 uppercase tracking-wide">{tLabels.cons}</p>
                          <div className="space-y-1">
                            <p className="text-xs text-red-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Richiede tempo e sforzo iniziale' : 'Requires initial time and effort'}</span>
                            </p>
                            <p className="text-xs text-red-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Stress a breve termine' : 'Short-term stress'}</span>
                            </p>
                            <p className="text-xs text-red-700 flex items-start gap-1">
                              <span>•</span>
                              <span>{lang === 'it' ? 'Incertezza economica iniziale' : 'Initial economic uncertainty'}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors">
                        {tLabels.seeFullAnalysis}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-indigo-600">{tLabels.decisionIntelligence}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">{t.features.decisions.title}</h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">{t.features.decisions.desc}</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.proConAnalysis}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.alignmentPercentage}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.consciousDecisions}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                PHASE 4: COMPLETE STORY (Living Guided)
                ======================================== */}

            {/* 7. Your Evolution Showcase - MERGED: Profile + Vital Balance + Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-violet-600">
                    {tLabels.yourEvolution}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.trackHolisticGrowth}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.evolutionDescriptionFull}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {tLabels.fiveDimensionsRadarFull}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {tLabels.emotionFlowTrendFull}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {tLabels.rhythmTrackingFull}
                    </span>
                  </div>
                </div>
              </div>
              {/* Evolution UI Mockup - Combined visualization */}
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-violet-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-8 shadow-lg">
                    <div className="mb-4">
                      <span className="text-lg font-black text-[#1D1D1F]">Synthesis</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs font-bold px-2 py-1 bg-violet-100 text-violet-700 rounded-full">EVOLUTION</span>
                        <span className="text-xs font-medium px-2 py-1 text-black/40">PROFILE</span>
                        <span className="text-xs font-medium px-2 py-1 text-black/40">TREND</span>
                      </div>
                    </div>
                    {/* Radar Chart */}
                    <div className="relative w-full aspect-square max-w-[200px] lg:max-w-[280px] mx-auto mb-4 lg:mb-6">
                      <svg viewBox="0 0 200 200" className="w-full h-full">
                        {/* Background pentagon */}
                        <polygon points="100,20 180,75 155,160 45,160 20,75" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                        <polygon points="100,45 165,85 145,145 55,145 35,85" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                        <polygon points="100,70 150,95 135,130 65,130 50,95" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                        {/* Data pentagon */}
                        <polygon points="100,45 160,80 145,145 60,140 40,90" fill="#8b5cf6" fillOpacity="0.3" stroke="#8b5cf6" strokeWidth="2"/>
                        {/* Labels */}
                        <text x="100" y="15" textAnchor="middle" fill="#6b7280" fontSize="10" fontWeight="bold">{tLabels.dimensionBody}</text>
                        <text x="185" y="80" textAnchor="start" fill="#6b7280" fontSize="10" fontWeight="bold">{tLabels.dimensionMind}</text>
                        <text x="160" y="175" textAnchor="middle" fill="#6b7280" fontSize="10" fontWeight="bold">{tLabels.dimensionHeart}</text>
                        <text x="40" y="175" textAnchor="middle" fill="#6b7280" fontSize="10" fontWeight="bold">{tLabels.dimensionSoul}</text>
                        <text x="15" y="80" textAnchor="end" fill="#6b7280" fontSize="10" fontWeight="bold">{tLabels.dimensionMatter}</text>
                      </svg>
                    </div>
                    {/* 5 Dimensions Stats */}
                    <div className="grid grid-cols-2 gap-2 lg:gap-3 mb-3 lg:mb-4">
                      {[
                        { labelKey: 'dimensionBody' as const, value: 61, trend: '+5%', color: 'from-red-400 to-red-500', textColor: 'text-red-600' },
                        { labelKey: 'dimensionMind' as const, value: 55, trend: '-2%', color: 'from-orange-400 to-orange-500', textColor: 'text-orange-600' },
                        { labelKey: 'dimensionHeart' as const, value: 77, trend: '+12%', color: 'from-blue-400 to-blue-500', textColor: 'text-blue-600' },
                        { labelKey: 'dimensionSoul' as const, value: 75, trend: '+8%', color: 'from-purple-400 to-purple-500', textColor: 'text-purple-600' }
                      ].map((dim, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-2 lg:p-3">
                          <p className="text-[9px] lg:text-[10px] font-bold text-gray-500 mb-1">{tLabels[dim.labelKey].toUpperCase()}</p>
                          <div className="flex items-baseline gap-1">
                            <span className="text-lg lg:text-xl font-black text-[#1D1D1F]">{dim.value}<span className="text-xs text-black/40">%</span></span>
                            <span className={`text-[10px] font-bold ${dim.textColor}`}>{dim.trend}</span>
                          </div>
                        </div>
                      ))}
                      <div className="bg-gray-50 rounded-lg p-2 lg:p-3 col-span-2">
                        <p className="text-[9px] lg:text-[10px] font-bold text-gray-500 mb-1">{tLabels.dimensionMatter.toUpperCase()}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg lg:text-xl font-black text-[#1D1D1F]">51<span className="text-xs text-black/40">%</span></span>
                          <span className="text-[10px] font-bold text-emerald-600">+3%</span>
                        </div>
                      </div>
                    </div>
                    {/* Best Time Indicator */}
                    <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-3 border border-violet-100">
                      <p className="text-xs font-bold text-violet-800 mb-1">
                        {tLabels.yourBestTime}
                      </p>
                      <p className="text-sm font-black text-[#1D1D1F]">
                        {tLabels.bestTimeDay}
                      </p>
                      <p className="text-xs text-black/60">
                        {tLabels.heartSoulPeak}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 7B. Goal Alignment Showcase - NEW */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Goal Alignment UI Component */}
              <div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-indigo-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-8 shadow-lg">
                    <div className="mb-4">
                      <span className="text-lg font-black text-[#1D1D1F]">Synthesis</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs font-medium px-2 py-1 text-black/40">PROFILE</span>
                        <span className="text-xs font-bold px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">TREND</span>
                        <span className="text-xs font-medium px-2 py-1 text-black/40">CONFIG</span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <h4 className="text-[11px] font-bold text-black/40 uppercase tracking-wider mb-1">
                        {tLabels.goalAlignmentTitle}
                      </h4>
                      <p className="text-[16px] md:text-[18px] font-black text-black">
                        {tLabels.goalAlignmentSubtitle}
                      </p>
                    </div>
                    {/* Goal Progress Ring Component */}
                    <GoalProgressRing
                      goals={[
                        {
                          name: tLabels.goalExample1,
                          progress: 68,
                          color: '#6366f1'
                        },
                        {
                          name: tLabels.goalExample2,
                          progress: 69,
                          color: '#8b5cf6'
                        },
                        {
                          name: tLabels.goalExample3,
                          progress: 68,
                          color: '#a855f7'
                        }
                      ]}
                      size={200}
                      strokeWidth={12}
                      translations={{
                        alignment: lang === 'it' ? 'Allineamento' : lang === 'es' ? 'Alineación' : lang === 'de' ? 'Ausrichtung' : lang === 'fr' ? 'Alignement' : 'Alignment',
                        addGoals: lang === 'it' ? 'Aggiungi obiettivi' : lang === 'es' ? 'Añadir objetivos' : lang === 'de' ? 'Ziele hinzufügen' : lang === 'fr' ? 'Ajouter des objectifs' : 'Add goals',
                        trending: lang === 'it' ? 'Tendenza' : lang === 'es' ? 'Tendencia' : lang === 'de' ? 'Trend' : lang === 'fr' ? 'Tendance' : 'Trending',
                        showMore: lang === 'it' ? 'Mostra altri' : lang === 'es' ? 'Mostrar más' : lang === 'de' ? 'Mehr anzeigen' : lang === 'fr' ? 'Afficher plus' : 'Show more',
                        showLess: lang === 'it' ? 'Mostra meno' : lang === 'es' ? 'Mostrar menos' : lang === 'de' ? 'Weniger anzeigen' : lang === 'fr' ? 'Afficher moins' : 'Show less'
                      }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-indigo-600">
                    {tLabels.goalAlignmentTitle}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.goalAlignmentSubtitle}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.goalAlignmentDesc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'Traccia fino a 3 obiettivi principali simultaneamente' : lang === 'es' ? 'Rastrea hasta 3 objetivos principales simultáneamente' : lang === 'de' ? 'Verfolge bis zu 3 Hauptziele gleichzeitig' : lang === 'fr' ? 'Suis jusqu\'à 3 objectifs principaux simultanément' : 'Track up to 3 main goals simultaneously'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'Visualizza percentuale di allineamento in tempo reale' : lang === 'es' ? 'Visualiza porcentaje de alineación en tiempo real' : lang === 'de' ? 'Echtzeit-Ausrichtungsprozentsatz anzeigen' : lang === 'fr' ? 'Affiche le pourcentage d\'alignement en temps réel' : 'Real-time alignment percentage visualization'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'L\'AI analizza i tuoi momenti per calcolare i progressi' : lang === 'es' ? 'La IA analiza tus momentos para calcular el progreso' : lang === 'de' ? 'KI analysiert deine Momente, um Fortschritte zu berechnen' : lang === 'fr' ? 'L\'IA analyse tes moments pour calculer les progrès' : 'AI analyzes your moments to calculate progress'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 7C. Weekly Rhythm Showcase - NEW */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-emerald-600">
                    {tLabels.weeklyRhythmTitle}
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.weeklyRhythmSubtitle}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.weeklyRhythmDesc}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'Heatmap settimanale del tuo benessere' : lang === 'es' ? 'Mapa de calor semanal de tu bienestar' : lang === 'de' ? 'Wöchentliche Heatmap deines Wohlbefindens' : lang === 'fr' ? 'Carte thermique hebdomadaire de ton bien-être' : 'Weekly heatmap of your wellbeing'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'Identifica i tuoi momenti migliori della settimana' : lang === 'es' ? 'Identifica tus mejores momentos de la semana' : lang === 'de' ? 'Identifiziere deine besten Momente der Woche' : lang === 'fr' ? 'Identifie tes meilleurs moments de la semaine' : 'Identify your best moments of the week'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">
                      {lang === 'it' ? 'Pattern di energia e umore visualizzati' : lang === 'es' ? 'Patrones de energía y humor visualizados' : lang === 'de' ? 'Energie- und Stimmungsmuster visualisiert' : lang === 'fr' ? 'Schémas d\'énergie et d\'humeur visualisés' : 'Energy and mood patterns visualized'}
                    </span>
                  </div>
                </div>
              </div>
              {/* Weekly Rhythm UI Component */}
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-emerald-100">
                  <WeeklyRhythmChart
                    data={[
                      { day: 3, hour: 'morning', value: 85, count: 3 },
                      { day: 4, hour: 'afternoon', value: 78, count: 2 },
                      { day: 5, hour: 'evening', value: 92, count: 4 },
                      { day: 5, hour: 'night', value: 88, count: 2 },
                      { day: 6, hour: 'afternoon', value: 75, count: 2 },
                      { day: 6, hour: 'evening', value: 90, count: 3 },
                      { day: 1, hour: 'afternoon', value: 68, count: 2 },
                      { day: 2, hour: 'morning', value: 55, count: 1 }
                    ]}
                    language={
                      lang === 'it' ? 'Italian' :
                      lang === 'es' ? 'Spanish' :
                      lang === 'de' ? 'German' :
                      lang === 'fr' ? 'French' :
                      'English'
                    }
                    translations={{
                      title: lang === 'it' ? 'Il Tuo Ritmo' : lang === 'es' ? 'Tu Ritmo' : lang === 'de' ? 'Dein Rhythmus' : lang === 'fr' ? 'Ton Rythme' : 'Your Rhythm',
                      subtitle: lang === 'it' ? 'Quando ti senti meglio' : lang === 'es' ? 'Cuándo te sientes mejor' : lang === 'de' ? 'Wann du dich am besten fühlst' : lang === 'fr' ? 'Quand tu te sens le mieux' : 'When you feel best',
                      bestMoment: lang === 'it' ? 'Momento migliore' : lang === 'es' ? 'Mejor momento' : lang === 'de' ? 'Bester Moment' : lang === 'fr' ? 'Meilleur moment' : 'Best time',
                      levelLow: lang === 'it' ? 'Basso' : lang === 'es' ? 'Bajo' : lang === 'de' ? 'Niedrig' : lang === 'fr' ? 'Bas' : 'Low',
                      levelMedium: lang === 'it' ? 'Medio' : lang === 'es' ? 'Medio' : lang === 'de' ? 'Mittel' : lang === 'fr' ? 'Moyen' : 'Medium',
                      levelHigh: lang === 'it' ? 'Alto' : lang === 'es' ? 'Alto' : lang === 'de' ? 'Hoch' : lang === 'fr' ? 'Élevé' : 'High'
                    }}
                  />
                </div>
              </div>
            </div>


            {/* 8. Life Intelligence Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Life Intelligence UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-purple-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-black text-[#1D1D1F]">Intellect</span>
                      </div>
                      <div className="flex items-center gap-2 mb-4">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                        <span className="text-xs font-black uppercase tracking-wide text-purple-600">{tLabels.lifeIntelligence}</span>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 mb-3">
                      <p className="text-sm text-[#1D1D1F] leading-relaxed mb-3">
                        {tLabels.intelligenceQuoteText}
                      </p>
                      <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-100">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="w-5 h-5 rounded bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs">⚖️</span>
                          </div>
                          <p className="text-xs font-bold text-yellow-800 uppercase tracking-wide">{tLabels.needsAttention}</p>
                        </div>
                        <p className="text-xs text-yellow-800 leading-relaxed">
                          {tLabels.intelligenceQuoteWarn}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-dashed border-gray-300">
                      <p className="text-xs text-black/60 italic">
                        "{tLabels.intelligenceQuoteQuestion}"
                      </p>
                    </div>
                    <div className="mt-3 text-xs text-black/40 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-400" />
                      <span>{tLabels.basedOnSources}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-purple-600">{tLabels.lifeIntelligence}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.deepProactiveInsights}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.aiProactiveDescription}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.insightsBasedHistory}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.proactiveQuestions}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.identifyTensions}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 9. Your Story Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                    <Search className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-amber-600">{tLabels.yourStory}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.findAnythingInstantly}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.searchContextDescription}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.semanticSearch}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.organizedCategories}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.momentConnectedGraph}</span>
                  </div>
                </div>
              </div>
              {/* Your Story UI Mockup */}
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-amber-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4">
                      <h3 className="text-xl font-black text-[#1D1D1F] mb-1">Your Story</h3>
                      <p className="text-xs text-black/40 font-medium">20 {tLabels.moments}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-black/40" />
                        <input
                          type="text"
                          placeholder={tLabels.searchPlaceholder}
                          className="text-sm text-black/50 bg-transparent outline-none flex-1"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                      <span className="text-xs font-bold px-3 py-1.5 bg-[#1D1D1F] text-white rounded-full whitespace-nowrap">{tLabels.allCategories}</span>
                      <span className="text-xs font-medium px-3 py-1.5 text-black/50 bg-gray-100 rounded-full whitespace-nowrap">Spirit</span>
                      <span className="text-xs font-medium px-3 py-1.5 text-black/50 bg-gray-100 rounded-full whitespace-nowrap">Finance</span>
                      <span className="text-xs font-medium px-3 py-1.5 text-black/50 bg-gray-100 rounded-full whitespace-nowrap">Connect</span>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-4 h-4 text-yellow-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-bold uppercase tracking-wide text-yellow-700 mb-1">{tLabels.today}</p>
                            <p className="text-sm text-[#1D1D1F] leading-relaxed">
                              {tLabels.actionTodayText}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-1">{tLabels.thisWeek}</p>
                            <p className="text-sm text-[#1D1D1F] leading-relaxed">
                              {tLabels.actionWeekText}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 10. Home Dashboard Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Home UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-blue-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="mb-4">
                      <p className="text-xs text-black/40 uppercase tracking-wide mb-1">Monday, Jan 5</p>
                      <h3 className="text-2xl font-black text-[#1D1D1F]">Hello, Alex</h3>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3 mb-4 border border-purple-100 flex items-center gap-2">
                      <Brain className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-purple-700">{tLabels.knowYouPercentage}</span>
                      <div className="flex gap-1 ml-auto">
                        {[1,2,3,4].map((i) => <div key={i} className="w-2 h-2 rounded-full bg-purple-400" />)}
                        <div className="w-2 h-2 rounded-full bg-gray-200" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-4 text-white">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm font-medium leading-relaxed mb-2">
                              {tLabels.joyQuoteText}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-blue-600">{tLabels.personalDashboard}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.journeyAtGlance}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.homeWelcomeDescription}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.emotionFlowChart}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.dimensionsTrends}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.identifyBestTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 11. Analytics Dashboard Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Analytics UI Mockup */}
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-cyan-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-8 shadow-lg">
                    <div className="mb-4">
                      <span className="text-lg font-black text-[#1D1D1F]">Synthesis</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs font-medium px-2 py-1 text-black/40">PROFILE</span>
                        <span className="text-xs font-bold px-2 py-1 bg-cyan-100 text-cyan-700 rounded-full">TREND</span>
                        <span className="text-xs font-medium px-2 py-1 text-black/40">TONE</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 mb-3">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-500 mb-3">{tLabels.emotionFlow}</p>
                      <p className="text-xs text-black/50 mb-2">{tLabels.lastDaysTracked}</p>
                      <div className="relative h-32">
                        <svg className="w-full h-full" viewBox="0 0 280 100" preserveAspectRatio="none">
                          {/* Grid lines */}
                          <line x1="0" y1="25" x2="280" y2="25" stroke="#E5E7EB" strokeWidth="0.5"/>
                          <line x1="0" y1="50" x2="280" y2="50" stroke="#E5E7EB" strokeWidth="0.5"/>
                          <line x1="0" y1="75" x2="280" y2="75" stroke="#E5E7EB" strokeWidth="0.5"/>
                          {/* Emotion flow path */}
                          <path
                            d="M0,60 L40,55 L80,45 L120,35 L160,30 L200,40 L240,50 L280,55"
                            fill="url(#emotionGradient)"
                            fillOpacity="0.2"
                            stroke="#8b5cf6"
                            strokeWidth="2"
                          />
                          <defs>
                            <linearGradient id="emotionGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.6"/>
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[8px] text-black/30 px-1">
                          <span>31 Dec</span>
                          <span>1 Jan</span>
                          <span>2 Jan</span>
                          <span>3 Jan</span>
                          <span>4 Jan</span>
                          <span>5 Jan</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 lg:gap-3">
                      {[
                        { label: 'BODY', value: 61, trend: '+5%', color: 'red' },
                        { label: 'MIND', value: 55, trend: '-2%', color: 'orange' },
                        { label: 'HEART', value: 77, trend: '+12%', color: 'blue' },
                        { label: 'SOUL', value: 75, trend: '+8%', color: 'purple' }
                      ].map((dim, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-2 lg:p-3">
                          <p className="text-[9px] lg:text-[10px] font-bold text-gray-500 mb-1">{dim.label}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-lg lg:text-xl font-black text-[#1D1D1F]">{dim.value}<span className="text-xs text-black/40">%</span></span>
                            <span className={`text-[10px] font-bold ${dim.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>{dim.trend}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Analytics Text Content */}
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-cyan-600">{tLabels.analyticsLabel}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.analyticsTitle}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.analyticsDescription}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.analyticsBullet1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.analyticsBullet2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.analyticsBullet3}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 11. Challenges Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Challenges UI Mockup */}
              <div>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-4 lg:p-8 shadow-2xl border border-orange-100">
                  <div className="bg-white rounded-2xl p-4 lg:p-7 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-600" />
                        <span className="text-sm font-black uppercase tracking-wide text-gray-500">{tLabels.challengesToOvercome}</span>
                      </div>
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">9</span>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                            <Target className="w-5 h-5 text-orange-600" />
                          </div>
                          <span className="text-xs font-bold text-orange-600 bg-white px-2 py-1 rounded-full">8/10</span>
                        </div>
                        <p className="text-sm font-black text-[#1D1D1F] mb-2">
                          {tLabels.challengeExample1Full}
                        </p>
                        <p className="text-xs text-black/60 mb-3">
                          {tLabels.challengeDescription1Full}
                        </p>
                        <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-1 rounded-full">{tLabels.situational}</span>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="text-xs font-bold text-blue-600 bg-white px-2 py-1 rounded-full">8/10</span>
                        </div>
                        <p className="text-sm font-black text-[#1D1D1F] mb-2">
                          {tLabels.challengeExample2Full}
                        </p>
                        <p className="text-xs text-black/60 mb-3">
                          {tLabels.challengeDescription2Full}
                        </p>
                        <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded-full">{tLabels.external}</span>
                      </div>
                    </div>
                    <div className="mt-4 text-center text-xs text-black/40 font-medium">
                      {tLabels.swipeForChallenges}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-wider text-orange-600">{tLabels.identifiedChallenges}</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 text-[#1D1D1F]">
                  {tLabels.faceRealObstacles}
                </h3>
                <p className="text-lg text-black/60 leading-relaxed mb-6 font-medium">
                  {tLabels.challengesAiDescription}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.challengeCategories}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.quantifiedIntensity}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{tLabels.automaticPrioritization}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works - Timeline */}
          <div className="mt-24">
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-12 text-[#1D1D1F]">
              {t.howItWorks.title}
            </h3>
            <div className="relative max-w-3xl mx-auto">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-[2px] bg-gradient-to-b from-indigo-500 via-purple-500 to-emerald-500 hidden sm:block" />

              {/* Steps */}
              {[
                { step: t.howItWorks.step1, icon: MessageCircle, color: 'from-indigo-500 to-indigo-600' },
                { step: t.howItWorks.step2, icon: Brain, color: 'from-purple-500 to-purple-600' },
                { step: t.howItWorks.step3, icon: Sparkles, color: 'from-emerald-500 to-emerald-600' }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 sm:gap-10 mb-12 last:mb-0">
                  <div className="relative shrink-0">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-xl`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-lg flex items-center justify-center text-[11px] font-black text-[#1D1D1F] border border-black/[0.04]">
                      {idx + 1}
                    </span>
                  </div>
                  <div className="pt-3">
                    <h4 className="text-xl sm:text-2xl font-black tracking-tight mb-2 text-[#1D1D1F]">{item.step.title}</h4>
                    <p className="text-black/60 text-lg leading-relaxed font-medium">{item.step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Personality Showcase */}
      <section ref={personalitiesSection.ref} className={`py-24 sm:py-32 bg-gradient-to-b from-white to-gray-50 ${personalitiesSection.isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-indigo-600 mb-4 block">
              {tLabels.yourAiGuides}
            </span>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4 text-[#1D1D1F]">
              {tPersonalities.title}
            </h2>
            <p className="text-lg text-black/60 max-w-2xl mx-auto font-medium">
              {tPersonalities.subtitle}
            </p>
          </div>

          {/* Personalities Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {Object.values(AI_PERSONALITIES).map((personality) => {
              // Map LandingLanguage to Language type
              const languageMap: Record<LandingLanguage, Language> = {
                'en': 'English',
                'it': 'Italian',
                'es': 'Spanish',
                'de': 'German',
                'fr': 'French'
              };
              const personalityLang = languageMap[lang];
              const name = personality.names[personalityLang] || personality.names.English;
              const shortDesc = personality.shortDescriptions[personalityLang] || personality.shortDescriptions.English;

              return (
                <div
                  key={personality.key}
                  className="group bg-white rounded-2xl p-6 border border-black/5 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                    {personality.emoji}
                  </div>
                  <h3 className="text-base font-black tracking-tight mb-2 text-[#1D1D1F]">
                    {name}
                  </h3>
                  <p className="text-xs text-black/50 font-medium leading-relaxed">
                    {shortDesc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" ref={pricingSection.ref} className={`py-24 sm:py-32 bg-white ${pricingSection.isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4 text-[#1D1D1F]">
              {tPricing.title}
            </h2>
            <p className="text-lg text-black/60 mb-8 font-medium">
              {tPricing.subtitle}
            </p>

            {/* Monthly/Annual Toggle */}
            <div className="inline-flex items-center gap-4 p-1 bg-gray-100 rounded-full">
              <button
                onClick={() => setPricingToggle('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                  pricingToggle === 'monthly' ? 'bg-white shadow-sm text-[#1D1D1F]' : 'text-black/50'
                }`}
              >
                {tPricing.monthly}
              </button>
              <button
                onClick={() => setPricingToggle('annual')}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                  pricingToggle === 'annual' ? 'bg-white shadow-sm text-[#1D1D1F]' : 'text-black/50'
                }`}
              >
                {tPricing.annual}
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{tPricing.savePercent}</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-gray-300 transition-all">
              <h3 className="text-2xl font-black mb-2">{tPricing.free.name}</h3>
              <p className="text-4xl font-black mb-6 text-[#1D1D1F]">
                {tPricing.free.price}
              </p>
              <button
                onClick={onRegister || onLogin}
                className="w-full py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 font-bold text-lg hover:bg-indigo-50 transition-all mb-8"
              >
                {tPricing.free.cta}
              </button>
              <ul className="space-y-3">
                {tPricing.free.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-black/70 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium Plan */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 border-2 border-indigo-700 relative overflow-hidden shadow-2xl shadow-indigo-200 lg:-mt-4 lg:scale-105">
              <div className="absolute top-4 right-4 bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-black">
                {tPricing.premium.badge}
              </div>
              <h3 className="text-2xl font-black mb-2 text-white">{tPricing.premium.name}</h3>
              <p className="text-4xl font-black mb-2 text-white">
                {pricingToggle === 'monthly' ? tPricing.premium.priceMonthly : tPricing.premium.priceAnnual}
              </p>
              {pricingToggle === 'annual' && (
                <p className="text-white/60 line-through mb-4">{tPricing.premium.priceAnnualFull}</p>
              )}
              <button
                onClick={onRegister || onLogin}
                className="w-full py-4 rounded-xl bg-white text-indigo-600 font-bold text-lg hover:bg-gray-100 transition-all mb-8 shadow-xl"
              >
                {tPricing.premium.cta}
              </button>
              <ul className="space-y-3">
                {tPricing.premium.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center mt-8 text-sm text-black/50 font-medium">
            {tPricing.noCreditCard} • {tPricing.cancelAnytime}
          </p>
        </div>
      </section>

      {/* Trust & Privacy Section - Apple-Inspired Redesign */}
      <section ref={trustSection.ref} className={`py-32 bg-gradient-to-b from-white via-blue-50/30 to-white ${trustSection.isVisible ? 'animate-scale-in' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Hero Header - Emotivo e Rassicurante */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 mb-6 shadow-2xl shadow-emerald-200">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-4 text-[#1D1D1F]">
              {tPrivacy.title}
            </h2>
            <p className="text-xl text-black/60 max-w-3xl mx-auto leading-relaxed">
              {tPrivacy.subtitle}
            </p>
          </div>

          {/* Privacy Principles - Card Grandi con Icone */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">

            {/* Card 1: Encryption */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-emerald-100 hover:border-emerald-200">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-100 rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-5 shadow-lg">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black mb-3 text-[#1D1D1F]">
                  {tPrivacy.encryption.title}
                </h3>
                <p className="text-black/60 leading-relaxed mb-4">
                  {tPrivacy.encryption.desc}
                </p>
                <div className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                  <Check className="w-4 h-4" />
                  <span>{tPrivacy.encryption.badge}</span>
                </div>
              </div>
            </div>

            {/* Card 2: No Data Selling */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100 hover:border-blue-200">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-100 rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mb-5 shadow-lg">
                  <XIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black mb-3 text-[#1D1D1F]">
                  {tPrivacy.noSelling.title}
                </h3>
                <p className="text-black/60 leading-relaxed mb-4">
                  {tPrivacy.noSelling.desc}
                </p>
                <div className="flex items-center gap-2 text-sm font-bold text-blue-700">
                  <Check className="w-4 h-4" />
                  <span>{tPrivacy.noSelling.badge}</span>
                </div>
              </div>
            </div>

            {/* Card 3: Full Control */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-purple-100 hover:border-purple-200">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-100 rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mb-5 shadow-lg">
                  <ArrowRight className="w-7 h-7 text-white transform rotate-90" />
                </div>
                <h3 className="text-2xl font-black mb-3 text-[#1D1D1F]">
                  {tPrivacy.control.title}
                </h3>
                <p className="text-black/60 leading-relaxed mb-4">
                  {tPrivacy.control.desc}
                </p>
                <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                  <Check className="w-4 h-4" />
                  <span>{tPrivacy.control.badge}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Badges - Visual Trust */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-12 text-center mb-16">
            <p className="text-sm font-black uppercase tracking-wider text-black/40 mb-6">
              {tPrivacy.compliance}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md">
                <Shield className="w-5 h-5 text-emerald-600" />
                <span className="font-bold text-sm">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-sm">ISO 27001</span>
              </div>
              <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md">
                <Check className="w-5 h-5 text-purple-600" />
                <span className="font-bold text-sm">SOC 2 Type II</span>
              </div>
            </div>
          </div>

          {/* Human-First Statement */}
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-lg text-black/70 leading-relaxed italic">
              {tPrivacy.humanStatement}
            </p>
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqSection.ref} className={`py-24 sm:py-32 bg-[#FAFAFA] ${faqSection.isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-12 text-center text-[#1D1D1F]">
            {tFaq.title}
          </h2>

          <div className="space-y-4">
            {tFaq.questions.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-lg text-[#1D1D1F] pr-4">{item.q}</span>
                  {expandedFaq === idx ? (
                    <Minus className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  ) : (
                    <Plus className="w-5 h-5 text-black/40 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-6 text-black/70 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 animate-gradient" />
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6 text-white leading-tight">
            {t.finalCta.title}
          </h2>
          <p className="text-xl text-white/80 mb-12 font-medium">
            {t.finalCta.subtitle}
          </p>

          <button
            onClick={onRegister || onLogin}
            className="group relative inline-flex items-center justify-center gap-3 px-12 py-6 text-lg font-black rounded-2xl bg-white text-[#1D1D1F] hover:bg-gray-100 transition-all active:scale-95 shadow-2xl"
          >
            <Sparkles className="w-5 h-5" />
            {t.finalCta.button}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="mt-8 text-white/50 text-sm font-medium flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            {tHeroRot.trustBadge}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-black/[0.04] bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <EgovexLogo size={32} />
              <span className="font-black text-lg tracking-tighter">Egovex</span>
              <span className="text-black/30 text-sm font-medium ml-2">{t.footer.tagline}</span>
            </div>

            <div className="flex items-center gap-6 text-sm text-black/40 font-medium">
              <button onClick={onShowPrivacy} className="hover:text-black transition-colors">{t.footer.privacy}</button>
              <button onClick={onShowTerms} className="hover:text-black transition-colors">{t.footer.terms}</button>
              <span>© {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingView;
